// Hideo - the active profile's data for Vue components, kept up to date.
// The data object is the store's own (the code changes it in place and saves it): a computed returning it would
// return the SAME object after a save, and computeds that depend on it would not run again. This shallowRef is triggered
// on every save (store.rev) and every refresh (ui.tick, renderAll / refreshVue), so everything derived from
// `data.value` recomputes. Write through saveAppData (or a store action), never by replacing data.value.
// One ref and one watcher are shared by every component (per data store, so tests with a fresh Pinia get their own):
// before, each of the ~30 components read and normalised the data again at every save.
import { effectScope, shallowRef, triggerRef, watch, type ShallowRef } from 'vue'
import { useDataStore } from '../stores/data'
import { useUiStore } from '../stores/ui'
import type { ProfileData } from '../data/types'

const shared = new WeakMap<object, ShallowRef<ProfileData>>()

export function useProfileData(): ShallowRef<ProfileData> {
  const store = useDataStore()
  let data = shared.get(store)
  if (!data) {
    const ui = useUiStore(), ref = shallowRef<ProfileData>(store.getData())
    // Detached: the watcher outlives the component that created it
    effectScope(true).run(() => watch(() => [store.rev, ui.tick], () => {
      ref.value = store.getData()
      triggerRef(ref)
    }))
    shared.set(store, data = ref)
  }
  return data
}
